import {Form} from '@/plugins/FWForms/index';
import type {AxiosError} from 'axios';
import axios from 'axios';
import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {StorageService} from '@/services/storage.service';
import * as types from '../mutation-types';
import {createOrderUrlQueryString} from '@/utils/urls';
import type {TripPhotoInterface} from '@/types';
import type {StoreLoaders} from '@/types/common';
import type {BookingFilter} from '@/types/booking';
import {type BookingInterface, BookingStatus} from '@/types/booking';
import type {ListingInterface} from '@/types/listing';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {Module} from 'vuex';
import type {RootState} from '@/types/RootState';
import {push} from 'notivue'
import {handleApiError} from '@/utils/errorHandler.ts';
import type {DateTimeString} from '@/types/order.ts';
import {d} from '@/utils/consoleHelper.ts';
import type {PaymentInterface} from '@/types/payment.ts';
import type {Automation, AutomationEvent} from '@/types/automation';


dayjs.extend(duration);

export type CancelBookingPayload = {
    b_id: string;
    refund: boolean;
    unblockTuro: boolean;
    sendCancellationEmail: boolean;
    sendDeclineEmail?: boolean;
    declineReason?: string;
}

export type SortItem = { key: string, order?: boolean | 'asc' | 'desc' }

export type BookingStatusChange = {
    b_id: string;
    status: BookingStatus;
}

export type RememberedCustomer = {
    email?: string;
    stripe_id?: string;
    last4?: string | null;
}

export type BookingActivityLog = {
    created_at?: string;
    description?: string;
    properties?: unknown;
}

export type BookingAutomationEvent = AutomationEvent & {
    automation: Automation;
    message?: string;
}

export type StoredTuroBooking = {
    uuid?: string;
    complete?: number;
    status?: string;
    start_trip?: string | number;
    start_trip_display?: string;
    end_trip_display?: string;
    turo_user?: {
        uuid?: string;
        email?: string;
        turo_id?: string;
    };
}

export type FleetResourceQueryOptions = {
    startDate?: string;
    endDate?: string;
    company_uuid?: string;
}

type BookingVehicleSwapData = {
    b_id: string,
    listing: ListingInterface,
    transferListingData: boolean,
    transferUnavailabilityOnCarSharing: boolean,
}

export type BookingVehicleSwapStep2Data = BookingVehicleSwapData & {
    booking: BookingInterface;
}

export type BookingFormError = string | Record<string, string[]>;

export type BookingQueryOptions = {
    page?: number,
    sortDesc?: string | boolean | boolean[];
    itemsPerPage?: number,
    includeAbandoned?: boolean,
    onlyAbandoned?: boolean,
    withOrderProperties?: boolean,
    withRenterProperties?: boolean,
    searchTerm?: string,
    searchTopic?: string,
    exporting?: boolean,
    providers?: string[],
    statuses?: string[],
    filter?: BookingFilter,
    sortBy?: SortItem[],
}

type PreparedBookingQueryData = {
    page: number;
    itemsPerPage: number;
    includeAbandoned?: boolean;
    onlyAbandoned?: boolean;
    withOrderProperties?: boolean;
    withRenterProperties?: boolean;
    searchTerm?: string;
    searchTopic?: string;
    exporting?: boolean;
    providers: string[];
    statuses?: string;
    filter?: BookingFilter;
    sortBy?: string[];
    filterQuery?: string;
}

function prepareBookingData(options: BookingQueryOptions = {}): PreparedBookingQueryData {
    const {
        page = 1,
        itemsPerPage = 20,
        includeAbandoned = false,
        onlyAbandoned = false,
        withOrderProperties,
        withRenterProperties,
        searchTerm,
        searchTopic,
        exporting,
        providers = [],
        statuses = [],
        filter,
        sortBy
    } = options;

    const data: PreparedBookingQueryData = {
        page,
        itemsPerPage,
        includeAbandoned,
        onlyAbandoned,
        withOrderProperties,
        withRenterProperties,
        searchTerm,
        searchTopic,
        exporting,
        providers,
    };

    if (statuses.length > 0) {
        data.statuses = statuses.join(',');
    }

    if (providers.length > 0) {
        data.statuses = providers.join(',');
    }

    if (!includeAbandoned) {
        delete data.includeAbandoned;
    }

    if (!onlyAbandoned) {
        delete data.onlyAbandoned;
    }

    if (filter) {
        data.filter = filter;
        data.filterQuery = Object.entries(filter)
            .filter(([_, value]) => value)
            .map(([key, value]) => `filter[${key}]=${value}`)
            .join('&');
    }

    if (sortBy) {
        data.sortBy = sortBy.map((item: SortItem) => {
            if ((item.order === 'desc' || item.order === true) && item.key) {
                return `-${item.key}`;
            }
            return item.key;
        });
    }

    return data;
}

export type CalendarAttribute = {
    key: string,
    id?: number,
    highlight: {
        color: string,
        fillMode: string,
    },
    dates: string | Dayjs | Date | string[],
    popover: {
        label: string,
    },
    bar?: string,
    trip_start?: string,
    trip_end?: string,
    trip_start_buffer?: string,
    trip_end_buffer?: string,
}


export interface BookingMeta {
    current_page: number;
    from: number | null;
    last_page: number;
    links: Array<{ url: string | null, label: string, active: boolean }>;
    path: string;
    per_page: number;
    stats: {
        count: {
            [key: string]: number,
        },
        payment_status: {
            count: {
                [key: string]: number,
            }
        },
        sources: {
            count: {
                [key: string]: number,
            }
        },
        statuses: {
            count: {
                [key: string]: number,
            }
        },
        tag_list: string[],
        total: number,
        totalAll?: number,
    }
    to: number | null;
    total: number;
    total_bookings: number;
    pending_request_overlaps?: Array<{
        listing_id: string;
        listing_name: string;
        overlapping_groups: string[][];
    }> | null;
}

export type vTableOptions = {
    groupBy: string[],
    page: number,
    itemsPerPage: number,
    sortBy: SortItem[],
    search: string | undefined,
}

export type UnavailableDates = {
    id?: number,
    trip_start: DateTimeString,
    trip_end: DateTimeString,
    trip_start_buffer: DateTimeString,
    trip_end_buffer: DateTimeString,
}

// [ { "key": "-starts_at", "order": "desc" } ]
export type BookingState = {
    dialogs: Record<string, boolean>,
    loading: StoreLoaders,
    init: boolean,
    notFound: boolean,
    listing: ListingInterface | null,
    statusFilterItems: string[],
    startsAtFilterItems: string | null,
    statusFilterTemp: string[],
    paymentStatusFilterItems: string[],
    activityFilter: SortItem[],
    sourcesFilter: string[],
    autoExtendFilter: boolean,
    booking: BookingInterface,
    priorityBookings: BookingInterface[],
    bookings: BookingInterface[],
    meta: BookingMeta | null,
    fleetResources: BookingInterface[],
    abandoned_bookings: BookingInterface[],
    totalBookings: number,
    customerBooking: Partial<BookingInterface>,
    calendarAttributes: CalendarAttribute[],
    unavailableDates: UnavailableDates[],
    completeBooking: {
        b_id: string,
    },
    turoBookings: StoredTuroBooking[],
    rememberedCustomer: RememberedCustomer,
    res2: unknown,
    bookingForm: Form<Record<string, unknown>>,
    bookingPayments: PaymentInterface[],
    bookingActivityLogs: BookingActivityLog[],
    automationEvents: BookingAutomationEvent[],
    orderTableOptions: vTableOptions,
    orderSearch: {
        search: string,
        searchTopic: string,
    },
    tripPhotos: TripPhotoInterface[],
    overlapping_pending_booking_ids: string[],
    overlapping_booking_ids: string[],
    overlapping_unavailable_booking_ids: string[],
}


const state: BookingState = {
    init: false,
    notFound: false,

    dialogs: {
        createTripExtensionDialog: false,
    },

    loading: {
        booking: false,
        booking_logs: false,
        bookings: false,
        meta: false,
        priority_bookings: false,
        fleet_resources: false,
        manualCheckout: false,
        check_vehicles_availability: false,
        updateBooking: false,
        extendBooking: false,
        deleteExtension: false,
        cancelBooking: false,
        bookingPayments: false,
        bookingStatus: false,
        addOrderItem: false,
        startBookingVehicleSwap: false,
        startBookingVehicleSwapStep2: false,
        attachCommissionPartner: false,
        getAutomationEvents: false,
        updateBookingCustomerFields: false,
        manuallyRemoveBooking: false,
        deleteTripPhotos: false,
        getTripPhotos: false,
        resyncTripPhotos: false,
    },

    bookingForm: new Form(),

    listing: null,

    customerBooking: {},

    totalBookings: 0,
    priorityBookings: [],
    bookings: [],
    abandoned_bookings: [],

    booking: {
        init: false,
        complete: 0,
        created_at: '',
        days: 0,
        delivery_price: 0,
        discount: 0,
        extras_price: 0,
        manually_deleted_at: null,
        booking_meta: null,
        payment_status: '',
        price_per_day: 0,
        r_id: '',
        renter: null,
        required_options_price: 0,
        source: '',
        status: BookingStatus.PENDING,
        taxes: 0,
        timezone: '',
        total: 0,
        trip_end: '',
        trip_end_tz: '',
        trip_start: '',
        trip_start_tz: '',
        turo_trip: null,
        updated_at: '',
        method: '',
        miles_extra: 0,
        miles_included: 0,

        b_id: null,
        l_id: null,
        cartId: null,

        host: null,

        customer: {
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            dob: '',
            age: '',
            signaturePng: '',
            signFullName: '',
            signInitials: '',
            agreeAdoptSignature: false,
            agreeToS: false,
        },

        settings: {
            zip_code: '',
            age_requirement: 0,
            mile_allowance: {
                miles_included: 0,
                miles_extra: 0,
                miles_extra_selected: false,
            },

            young_driver: 0,
            description: '',
        },

        vehicle: {
            available: false,
            price: 0,
            title: '',
            color: '',
            year: '',
            image: '',
            images: [],
            duration: '',
            miles_included: '',
            lat: '',
            long: '',
        },

        listing: null,

        pricing: {
            original_price: 0,
            price: 0,
            price_type: 'flat_fee',
            price_period: 'Day',

            discounts: {
                success: false,
                loading: false,
                discounted: false,
                code: '',
                discount: '',
                discountValue: 0,
                newPrice: 0,
                type: '',
                reason: '',
            },

            delivery: 0,
            extras: 0,
            requiredOption: 0,
            total_per_day: 0,
            subTotal: 0,
            grandTotal: 0,
            paymentDisplay: '',
            security_deposit: 0,
        },

        trip_required_options: [],

        trip_displays: {
            tripMilesUnlimited: false,
            tripMilesIncluded: '',
            tripMilesOverageDisplay: '',
            tripMilesHelpModalDisplay: '',

            young_driver: {
                helpModalDisplaySet: false,
                helpModalDisplayH1: '',
                helpModalDisplayP1: '',
            },
        },

        mile_allowance: {
            miles_included: '',
            miles_extra: '',
            miles_extra_selected: false,
        },

        duration: {
            months: 0,
            weeks: 0,
            days: 1,
            hours: 0,
            minutes: 0,
            units: 1,
        },

        location: {
            city: '',
            state: '',
            zip_code: '',
            neighborhood: '',
            airport_code: '',
            price: 0,
            type: '',
            selecting: false,
            count: JSON.parse(localStorage.getItem('count') || '0'),
        },

        locations: [],

        delivery: {},
    },

    meta: null,
    bookingPayments: [],
    bookingActivityLogs: [],

    completeBooking: {
        b_id: '',
    },

    unavailableDates: [],
    calendarAttributes: [
        {
            key: 'today',
            highlight: {
                color: 'orange',
                fillMode: 'light',
            },
            dates: new Date(),
            popover: {
                label: 'Today',
            },
        },
    ],

    turoBookings: [],

    rememberedCustomer: {},
    res2: null as unknown,
    automationEvents: [],
    fleetResources: [],
    statusFilterItems: [] as string[],
    startsAtFilterItems: null,
    statusFilterTemp: [],
    paymentStatusFilterItems: [],
    sourcesFilter: [] as string[],
    autoExtendFilter: false,
    activityFilter: [{'key': '-created_at', 'order': 'desc'}],

    orderTableOptions: {
        groupBy: [],
        page: 1,
        itemsPerPage: 20,
        sortBy: [{'key': 'created_at', 'order': 'desc'}],
        search: undefined,
    },
    orderSearch: {
        search: '',
        searchTopic: 'b_id',
    },
    tripPhotos: [],
    overlapping_pending_booking_ids: [] as string[],
    overlapping_booking_ids: [] as string[],
    overlapping_unavailable_booking_ids: [] as string[],
};

const getters = {
    init: (state: BookingState) => state.init,
    notFound: (state: BookingState) => state.notFound,
    statusFilterItems: (state: BookingState) => state.statusFilterItems,
    startsAtFilterItems: (state: BookingState) => state.startsAtFilterItems,
    statusFilterTemp: (state: BookingState) => state.statusFilterTemp,
    paymentStatusFilterItems: (state: BookingState) => state.paymentStatusFilterItems,
    autoExtendFilter: (state: BookingState) => state.autoExtendFilter,
    activityFilter: (state: BookingState) => state.activityFilter,
    sourcesFilter: (state: BookingState) => state.sourcesFilter,
    booking: (state: BookingState) => state.booking,
    priorityBookings: (state: BookingState) => state.priorityBookings,
    bookings: (state: BookingState) => state.bookings,
    meta: (state: BookingState) => state.meta,
    overlapping_pending_booking_ids: (state: BookingState) => state.overlapping_pending_booking_ids,
    overlapping_booking_ids: (state: BookingState) => state.overlapping_booking_ids,
    overlapping_unavailable_booking_ids: (state: BookingState) => state.overlapping_unavailable_booking_ids,
    fleetResources: (state: BookingState) => state.fleetResources,
    abandonedBookings: (state: BookingState) => state.abandoned_bookings,
    totalBookings: (state: BookingState) => state.totalBookings,
    customerBooking: (state: BookingState) => state.customerBooking,
    bookingLoading: (state: BookingState) => state.loading,
    calendarAttributes: (state: BookingState) => state.calendarAttributes,
    unavailableDates: (state: BookingState) => state.unavailableDates,
    bookingCustomer: (state: BookingState) => state.booking.customer,
    bookingVehicle: (state: BookingState) => state.booking.vehicle,
    completeBooking: (state: BookingState) => state.completeBooking,
    turoBookings: (state: BookingState) => state.turoBookings,
    rememberedCustomer: (state: BookingState) => state.rememberedCustomer,
    res2: (state: BookingState) => state.res2,
    bookingForm: (state: BookingState) => state.bookingForm,
    bookingPayments: (state: BookingState) => state.bookingPayments,
    bookingActivityLogs: (state: BookingState) => state.bookingActivityLogs,
    automationEvents: (state: BookingState) => state.automationEvents,
    bookingDialogs: (state: BookingState) => state.dialogs,
    orderTableOptions: (state: BookingState) => state.orderTableOptions,
    orderSearch: (state: BookingState) => state.orderSearch,
    tripPhotos: (state: BookingState) => state.tripPhotos,
};

const mutations = {
    [types.SET_BOOKING_LOADING](state: BookingState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },

    [types.SET_BOOKING_DIALOG](state: BookingState, dialog: { subject: string, value: boolean }) {
        state.dialogs[dialog.subject] = dialog.value;
    },
    [types.CLEAR_BOOKING_FORM_ERRORS](state: BookingState, form_field?: string | null) {
        if (form_field === null || form_field === undefined) {
            state.bookingForm.errors.clear();
        } else {
            state.bookingForm.errors.clear(form_field);
        }
    },
    [types.SET_BOOKING_FORM_ERROR](state: BookingState, error: BookingFormError) {
        state.bookingForm.errors.set(error);
    },
    [types.SET_CURRENT_WORKING_BOOKING_TRIP_PHOTOS](state: BookingState, tripPhotos: TripPhotoInterface[]) {
        state.tripPhotos = tripPhotos;
    },

    [types.SET_BOOKING_ACTIVITY_LOGS](state: BookingState, logs: BookingActivityLog[]) {
        state.bookingActivityLogs = logs ?? [];
    },
    [types.SET_LISTING_ID](state: BookingState, l_id: string) {
        state.booking.l_id = l_id;
    },
    [types.SET_ORDER_TABLE_OPTIONS](state: BookingState, payload: vTableOptions) {
        state.orderTableOptions = payload;
    },
    [types.SET_ORDER_SEARCH](state: BookingState, payload: { search: string, searchTopic: string }) {
        state.orderSearch = payload;
    },
    [types.SET_STATUS_FILTER_ITEMS](state: BookingState, statuses: string[]) {
        state.startsAtFilterItems = null;
        state.statusFilterItems = statuses;
        state.orderTableOptions.page = 1;
    },
    [types.SET_STATUS_FILTER_ITEMS_UPCOMING](state: BookingState) {
        state.statusFilterItems = ['reserved'];
        state.startsAtFilterItems = dayjs().format('YYYY-MM-DD HH:mm:ss');
        state.orderTableOptions.page = 1;
    },
    [types.SET_STATUS_FILTER_TEMP_ITEMS](state: BookingState, filter: string[]) {
        state.statusFilterTemp = filter;
    },
    [types.SET_ACTIVITY_FILTER_ITEMS](state: BookingState, activity: SortItem[]) {
        state.activityFilter = activity;

        state.orderTableOptions.sortBy = activity;
    },
    [types.SET_SOURCE_FILTER_ITEMS](state: BookingState, sources: string[]) {
        state.sourcesFilter = sources;
        state.orderTableOptions.page = 1;
    },
    [types.SET_AUTO_EXTEND_FILTER](state: BookingState, enabled: boolean) {
        state.autoExtendFilter = enabled;
        state.orderTableOptions.page = 1;
    },
    [types.SET_PAYMENT_STATUS_FILTER_ITEMS](state: BookingState, statuses: string[]) {
        state.paymentStatusFilterItems = statuses;
        state.orderTableOptions.page = 1;
    },
    [types.SET_BOOKING_CART_ID](state: BookingState, cartId: string) {
        state.booking.cartId = cartId;
    },
    [types.SET_CURRENT_WORKING_BOOKING](state: BookingState, booking: BookingInterface) {
        d.dir('SET_CURRENT_WORKING_BOOKING');
        d.dir(booking);
        state.booking = booking;
        state.init = true;
    },
    [types.SET_OVERLAPPING_PENDING_BOOKING_IDS](state: BookingState, ids: string[]) {
        state.overlapping_pending_booking_ids = ids ?? [];
    },
    [types.SET_OVERLAPPING_BOOKING_IDS](state: BookingState, ids: string[]) {
        state.overlapping_booking_ids = ids ?? [];
    },
    [types.SET_OVERLAPPING_UNAVAILABLE_BOOKING_IDS](state: BookingState, ids: string[]) {
        state.overlapping_unavailable_booking_ids = ids ?? [];
    },
    [types.SET_CURRENT_WORKING_BOOKING_STATUS](state: BookingState, status: BookingStatus) {
        state.booking.status = status;
    },
    [types.SET_CURRENT_WORKING_BOOKING_ATTRIBUTE](state: BookingState, data: {
        attribute: string;
        booking?: Partial<BookingInterface>;
    }) {
        if (!data?.attribute || !data.booking) {
            return;
        }
        Object.assign(state.booking, {[data.attribute]: data.booking[data.attribute as keyof BookingInterface]});
    },
    [types.SET_BOOKING_NOT_FOUND](state: BookingState, value: boolean) {
        state.notFound = value;
    },
    [types.SET_BOOKINGS](state: BookingState, bookings: BookingInterface[]) {
        state.bookings = bookings;
        state.init = true;
    },
    [types.SET_BOOKINGS_META](state: BookingState, meta: BookingMeta) {
        state.meta = meta;
    },
    [types.SET_ABANDONED_BOOKINGS](state: BookingState, bookings: BookingInterface[]) {
        state.abandoned_bookings = bookings;
    },
    [types.SET_PRIORITY_BOOKINGS](state: BookingState, bookings: BookingInterface[]) {
        state.priorityBookings = bookings;
    },
    [types.SET_FLEET_RESOURCES](state: BookingState, bookings: BookingInterface[]) {
        state.fleetResources = bookings;
    },
    [types.SET_BOOKING_AUTOMATION_EVENTS](state: BookingState, events: BookingAutomationEvent[]) {
        state.automationEvents = events;
    },
    [types.SET_TOTAL_BOOKINGS](state: BookingState, count: number) {
        state.totalBookings = count;
    },
    [types.RESET_BOOKING_STATES](state: BookingState) {
        state.totalBookings = 0;
        state.bookings = [];
    },
    [types.SET_BOOKING_PAYMENTS](state: BookingState, payments: PaymentInterface[]) {
        state.bookingPayments = payments;
    },
    [types.SET_CALENDAR_ATTRIBUTES](state: BookingState, attributes: CalendarAttribute[]) {
        state.calendarAttributes = attributes;
    },
    [types.PUSH_CALENDAR_ATTRIBUTES](state: BookingState, attribute: CalendarAttribute) {
        state.calendarAttributes.push(attribute);
    },
    [types.RESET_UNAVAILABLE_DATES](state: BookingState) {
        state.calendarAttributes = [
            {
                key: 'today',
                highlight: {
                    color: 'orange',
                    fillMode: 'light',
                },
                dates: new Date(),
                popover: {
                    label: 'Today',
                },
            },
        ];
    },
    [types.SET_UNAVAILABLE_DATES](state: BookingState, dates: UnavailableDates[] | undefined) {
        const unavailableDates = dates ?? [];
        state.unavailableDates = unavailableDates;

        state.calendarAttributes = [{
            key: 'today',
            highlight: {
                color: 'orange',
                fillMode: 'light',
            },
            dates: new Date(),
            popover: {
                label: 'Today',
            },
        },];

        const createCalendarAttribute = (barColor: string, date: string | Dayjs, label: string): CalendarAttribute => ({
            key: 'unavailable-' + date,
            highlight: {
                color: 'transparent',
                fillMode: 'light',
            },
            bar: barColor,
            dates: [dayjs(date).format()],
            popover: {label},
        });

        unavailableDates.forEach(date => {
            const days = dayjs.duration(dayjs(date.trip_end).diff(dayjs(date.trip_start))).asDays();
            const startTimeBuffer = dayjs(date.trip_start_buffer).format('h:mm a');
            const endTimeBuffer = dayjs(date.trip_end_buffer).format('h:mm a');

            if (days === 1) {
                state.calendarAttributes.push(
                    createCalendarAttribute('green', date.trip_start, `Becomes unavailable at ${startTimeBuffer}`),
                    createCalendarAttribute('red', date.trip_start, `Unavailable after ${startTimeBuffer}`),
                    createCalendarAttribute('red', date.trip_end, `Unavailable until ${startTimeBuffer}`),
                    createCalendarAttribute('green', date.trip_end, `Becomes available after ${endTimeBuffer}`),
                );
            } else {
                const middleDur: CalendarAttribute[] = [];
                for (let i = 1; i < days; i++) {
                    middleDur.push(createCalendarAttribute('red', dayjs(date.trip_start).add(i, 'days'), 'Unavailable'));
                }

                state.calendarAttributes.push(
                    createCalendarAttribute('green', date.trip_start, `Becomes unavailable at ${startTimeBuffer}`),
                    createCalendarAttribute('red', date.trip_start, `Unavailable after ${startTimeBuffer}`),
                    ...middleDur,
                    createCalendarAttribute('red', date.trip_end, `Unavailable until after ${endTimeBuffer}`),
                    createCalendarAttribute('green', date.trip_end, `Becomes available after ${endTimeBuffer}`),
                );
            }
        });
    },

    [types.SET_BOOKING_LISTING](state: BookingState, data: ListingInterface) {
        state.listing = data;

        if (state.listing) {
            state.listing.init = true;
        }

        if (data?.pricing) {
            state.booking.pricing = {
                ...state.booking.pricing,
                ...data.pricing,
                security_deposit: data.pricing.sec_dep ?? 0,
            };
        }
    },

    [types.SET_STORED_TURO_BOOKINGS](state: BookingState, bookings: StoredTuroBooking[]) {
        state.turoBookings = bookings;
    },
    [types.SET_CUSTOMER_BOOKING](state: BookingState, booking: Partial<BookingInterface>) {
        state.customerBooking = booking;
    },
    [types.SET_REMEMBER_CUSTOMER](state: BookingState, customer: RememberedCustomer) {
        const data: RememberedCustomer = {
            email: customer?.email,
            stripe_id: customer?.stripe_id,
            last4: null,
        };

        if (customer?.last4) {
            data.last4 = customer.last4;
        }
        state.rememberedCustomer = customer;
        localStorage.setItem('customer', JSON.stringify(data));
    },
    [types.RESET_BOOKING](state: BookingState) {
        state.booking = {
            fuel_level_end: 0,
            fuel_level_start: 0,
            listing: undefined,
            odometer_end: 0,
            odometer_start: 0,
            b_id: '',
            booking_meta: undefined,
            complete: 0,
            created_at: '',
            days: 0,
            delivery_price: 0,
            discount: 0,
            extras_price: 0,
            manually_deleted_at: '',
            method: '',
            miles_extra: 0,
            miles_included: 0,
            payment_status: '',
            price_per_day: 0,
            r_id: '',
            renter: undefined,
            required_options_price: 0,
            source: '',
            status: BookingStatus.PENDING,
            taxes: 0,
            timezone: '',
            total: 0,
            trip_end: '',
            trip_end_tz: '',
            trip_start: '',
            trip_start_tz: '',
            turo_trip: null,
            updated_at: '',
            init: false,
            l_id: null,
            cartId: null,
            host: null,
            customer: {
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                dob: '',
                age: '',
                signaturePng: '',
                signFullName: '',
                signInitials: '',
                agreeAdoptSignature: false,
                agreeToS: false,
            },
            settings: {
                zip_code: '',
                age_requirement: 0,
                mile_allowance: {
                    miles_included: 0,
                    miles_extra: 0,
                    miles_extra_selected: false,
                },

                young_driver: 0,
                description: '',
            },

            vehicle: {
                available: false,
                price: 0,
                title: '',
                color: '',
                year: '',
                image: '',
                images: [],
                duration: '',
                miles_included: '',
                lat: '',
                long: '',
            },

            pricing: {
                original_price: 0,
                price: 0,
                price_type: 'flat_fee',
                price_period: 'Day',

                discounts: {
                    success: false,
                    loading: false,
                    discounted: false,
                    code: '',
                    discount: '',
                    discountValue: 0,
                    newPrice: 0,
                    type: '',
                    reason: '',
                },

                delivery: 0,
                extras: 0,
                requiredOption: 0,
                total_per_day: 0,
                subTotal: 0,
                grandTotal: 0,
                paymentDisplay: '',
                security_deposit: 1,
            },

            trip_required_options: [],

            trip_displays: {
                tripMilesUnlimited: false,
                tripMilesIncluded: '',
                tripMilesOverageDisplay: '',
                tripMilesHelpModalDisplay: '',

                young_driver: {
                    helpModalDisplaySet: false,
                    helpModalDisplayH1: '',
                    helpModalDisplayP1: '',
                },
            },

            mile_allowance: {
                miles_included: '',
                miles_extra: '',
                miles_extra_selected: false,
            },

            duration: {
                months: 0,
                weeks: 0,
                days: 1,
                hours: 0,
                minutes: 0,
                units: 1,
            },

            location: {
                city: '',
                state: '',
                zip_code: '',
                neighborhood: '',
                airport_code: '',
                price: 0,
                type: '',
                selecting: false,
                count: JSON.parse(localStorage.getItem('count') || '0'),
            },

            locations: [],

            delivery: {}
        };
        state.overlapping_pending_booking_ids = [];
        state.overlapping_booking_ids = [];
        state.overlapping_unavailable_booking_ids = [];
    },
};

const actions = {

    async fetchPriorityBookings({commit}) {
        const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'priority_bookings', () => axios.get('/v1/bookings/priority'));
        commit(types.SET_PRIORITY_BOOKINGS, responseData.bookings);
    },

    async fetchSingleBooking({commit}, b_id: string) {
        commit(types.SET_BOOKING_NOT_FOUND, false);

        try {
            const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'booking', () => axios.get(`/v1/bookings/${b_id}`));


            const renter = responseData?.booking?.renter || [];
            const order = responseData?.booking?.order || [];

            commit('SET_CURRENT_WORKING_BOOKING', responseData.booking);
            commit(types.SET_OVERLAPPING_PENDING_BOOKING_IDS, responseData.overlapping_pending_booking_ids ?? []);
            commit(types.SET_OVERLAPPING_BOOKING_IDS, responseData.overlapping_booking_ids ?? []);
            commit(types.SET_OVERLAPPING_UNAVAILABLE_BOOKING_IDS, responseData.overlapping_unavailable_booking_ids ?? []);
            commit('renters/SET_RENTER', renter, {root: true});
            commit('order/SET_ORDER', order, {root: true});
        } catch (error) {
            const err = error as AxiosError;
            if (err?.response?.status === 404) {
                commit(types.SET_BOOKING_NOT_FOUND, true);
            }
        }
    },

    async getBookingActivityLogs({commit}, b_id: string) {
        const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'booking_logs', () => axios.get(`/v1/bookings/${b_id}/activity-logs`));
        const logs = responseData?.activity ?? [];
        commit(types.SET_BOOKING_ACTIVITY_LOGS, logs);
        return logs;
    },

    /**
     * Fetch fleet resources
     */
    fetchFleetResources({commit}, options: FleetResourceQueryOptions = {}) {
        commit('SET_BOOKING_LOADING', {subject: 'fleet_resources', loading: true});

        let qString = '?';
        if (options?.startDate) {
            qString += `startDate=${options.startDate}`;
        }
        if (options?.endDate) {
            qString += `&endDate=${options.endDate}`;
        }

        axios.get(`/v1/fleet-resources${qString}`)
            .then(response => {
                commit('SET_FLEET_RESOURCES', response?.data?.bookings);
            }).catch(() => {
        }).finally(() => {
            commit('SET_BOOKING_LOADING', {subject: 'fleet_resources', loading: false});
        });
    },

    /**
     * Fetch bookings.
     */
    async fetchBookings({commit}, options: BookingQueryOptions = {}) {
        try {
            const data = prepareBookingData(options);

            const query = createOrderUrlQueryString(data, true);

            const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'bookings', () => axios.get(`/v1/bookings${query}`));

            if (options?.onlyAbandoned) {
                commit(types.SET_ABANDONED_BOOKINGS, responseData.data);
            } else {
                commit(types.SET_BOOKINGS, responseData.data);
                commit(types.SET_BOOKINGS_META, responseData.meta);
                commit(types.SET_TOTAL_BOOKINGS, responseData.meta.total);
            }
        } catch (error) {
            const err = error as AxiosError;
            const message = (err?.response?.data as { message?: string })?.message || 'There was an error';
            commit('SET_BOOKING_FORM_ERROR', message);
        }
    },

    async fetchCompanyBooking({commit}, payload: string | { b_id: string; key?: string }) {
        const b_id = typeof payload === 'string' ? payload : payload.b_id;
        const key = typeof payload === 'string' ? '' : (payload.key ?? '');
        const url = `/v1/company/${StorageService.getCompanyUuid()}/booking/${b_id}`;

        try {
            // The guest key unlocks the recipients' signing keys in the payload;
            // without it the status page is read-only.
            const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'booking', () => (key ? axios.get(url, {params: {key}}) : axios.get(url)));

            commit(types.SET_CUSTOMER_BOOKING, responseData?.booking ?? {});
            const envelopes = responseData?.booking?.envelope || [];
            let lastEnvelope = {};
            if (envelopes.length > 0) {
                lastEnvelope = envelopes[envelopes.length - 1];
            }
            commit('envelope/SET_ENVELOPE', lastEnvelope, {root: true});

            return responseData;
        } catch (error) {
            const err = error as AxiosError;
            if (err?.response?.status === 404) {
                commit(types.SET_BOOKING_NOT_FOUND, true);
            }
        }
    },

    /**
     * Create a booking meta
     */
    async createBookingMeta({commit}, data: { booking: BookingInterface, key: string, value: unknown }) {
        return await performApiRequest(commit, 'SET_BOOKING_LOADING', 'meta', () => axios.post(`/v1/bookings/${data.booking.b_id}/meta`, {
            key: data.key,
            value: data.value,
        }));
    },

    fetchStoredTuroBookings({commit}) {
        axios.get('/v1/turo-bookings')
            .then(response => {
                commit('SET_STORED_TURO_BOOKINGS', response?.data?.bookings ?? []);
            }).catch(() => {
        }).finally(() => {
            commit('SET_BOOKING_LOADING', {subject: 'check_vehicles_availability', loading: false});
        });
    },

    updateListingId({commit}, l_id: string) {
        commit(types.SET_LISTING_ID, l_id);
    },

    async checkOneListingsBookings({commit}, l_id: string) {
        const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'check_vehicles_availability', () => axios.get(`/v1/company/${StorageService.getCompanyUuid()}/listings/${l_id}/availability`));
        commit('SET_UNAVAILABLE_DATES', responseData.unavailable);
    },

    /**
     * Update a booking
     *
     * @param commit
     * @param state
     * @param {String} id
     */
    retrieve: {
        root: true,
        handler({commit, state}, id) {
            axios.post(`/v1/company/${StorageService.getCompanyUuid()}/stripe/retrieve`, {
                id,
            }).then(response => {
                const checkoutSession = response?.data?.checkoutSession;
                state.res2 = response?.data?.$res2;

                commit('SET_REMEMBER_CUSTOMER', {
                    email: checkoutSession?.customer?.email,
                    stripe_id: checkoutSession?.customer?.id,
                    last4: checkoutSession?.setup_intent?.payment_method?.card?.last4,
                });
            }).catch(() => {
            });
        },
    },

    /**
     * Update a booking
     */
    async updateBooking({commit, state}, data: Partial<BookingInterface> & { b_id: string }) {
        try {
            commit('CLEAR_BOOKING_FORM_ERRORS');
            const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'updateBooking', () => axios.put(`/v1/bookings/${data.b_id}`, data));

            commit('SET_CURRENT_WORKING_BOOKING', responseData.booking);
            commit('renters/SET_RENTER', responseData?.booking?.renter || [], {root: true});
            push.success({
                message: 'Booking updated successfully',
            });
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as Record<string, string[]>)?.errors || [];
            handleApiError(error);
            state.bookingForm.errors.set(errors);
        }
    },

    /**
     * Update a booking custom field
     */
    async updateBookingCustomFields({commit}, data: { b_id: string, properties: Record<string, unknown> }) {
        commit('SET_BOOKING_LOADING', {subject: 'updateBookingCustomerFields', loading: true});

        try {
            try {
                await axios.put(`/v1/bookings/${data.b_id}/custom-fields`, {properties: data.properties});
                return true;
            } catch (e) {
                push.error({
                    message: (axios.isAxiosError(e) ? (e.response?.data as {
                        message?: string
                    })?.message : undefined) || 'There was an error',
                });
            }
        } finally {
            commit('SET_BOOKING_LOADING', {subject: 'updateBookingCustomerFields', loading: false});
        }
    },

    /**
     * Extend a booking
     */
    async extendBooking({commit}, data: { b_id: string, trip_end: string, comments: string }) {
        const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'extendBooking', () => axios.post(`/v1/bookings/${data.b_id}/extend`, data));
        commit(types.SET_CURRENT_WORKING_BOOKING, responseData.booking);
        return responseData;
    },

    /**
     * Delete a booking extension
     */
    async deleteExtension({commit}, extension_uuid: string) {
        const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'deleteExtension', () => axios.delete(`/v1/extensions/${extension_uuid}`));

        commit('SET_CURRENT_WORKING_BOOKING_ATTRIBUTE', {
            booking: responseData.booking,
            attribute: 'extensions'
        });
    },

    async attachCommissionPartner({commit}, data: { b_id: string, partner_uuid: string }) {
        commit('SET_BOOKING_LOADING', {subject: 'attachCommissionPartner', loading: true});
        commit('CLEAR_BOOKING_FORM_ERRORS');

        return axios.put(`/v1/bookings/${data.b_id}/attach-commission-partner/${data.partner_uuid}`, data).then(response => {
            commit('SET_CURRENT_WORKING_BOOKING', response?.data?.booking);

            return true;
        }).catch(error => {
            push.error({
                message: error?.response?.data?.message || 'There was an error',
            });

            return false;
        }).finally(() => {
            commit('SET_BOOKING_LOADING', {subject: 'attachCommissionPartner', loading: false});
        });
    },

    removeCommissionPartnerFromBooking({commit}, data: { b_id: string, partner_uuid: string }) {
        commit('SET_BOOKING_LOADING', {subject: 'attachCommissionPartner', loading: true});
        commit('CLEAR_BOOKING_FORM_ERRORS');

        return axios.delete(`/v1/bookings/${data.b_id}/commission/${data.partner_uuid}`)
            .then(response => {
                commit('SET_CURRENT_WORKING_BOOKING', response?.data?.booking);

                return true;
            }).catch(error => {
                push.error({
                    message: error?.response?.data?.message || 'There was an error',
                });

                return false;
            }).finally(() => {
                commit('SET_BOOKING_LOADING', {subject: 'attachCommissionPartner', loading: false});
            });
    },

    async cancelBooking({commit, dispatch}, data: CancelBookingPayload) {
        commit('CLEAR_BOOKING_FORM_ERRORS');

        try {
            const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'cancelBooking', () => axios.post(`/v1/bookings/${data.b_id}/cancel`, data));

            push.success({
                message: typeof data.sendDeclineEmail === 'boolean'
                    ? (data.sendDeclineEmail
                        ? 'Booking request declined and customer notified'
                        : 'Booking request declined')
                    : 'Booking canceled successfully',
            });

            if (responseData.booking) {
                commit('SET_CURRENT_WORKING_BOOKING', responseData.booking);
            } else {
                dispatch('fetchSingleBooking', data.b_id);
            }

            return responseData.booking ?? true;
        } catch (error) {
            handleApiError(error);

            return undefined;
        }
    },


    async requestCancelBooking({commit}, data: { bookingId: string }) {
        const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'cancelBooking', () => axios.post('/v1/request-cancellation', data));

        return responseData.booking ?? true;
    },

    async fetchBookingPayments({commit}, b_id: string) {
        commit('CLEAR_BOOKING_FORM_ERRORS');

        try {
            const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'bookingPayments', () => axios.get('/v1/payments', {params: {b_id}}));
            commit(types.SET_BOOKING_PAYMENTS, responseData?.payments, []);
        } catch (error) {
            handleApiError(error);
        }
    },

    changeBookingStatus({commit}, data: BookingStatusChange) {
        commit('SET_BOOKING_LOADING', {subject: 'bookingStatus', loading: true});
        commit('CLEAR_BOOKING_FORM_ERRORS');

        return axios.put(`/v1/bookings/${data.b_id}/status`, data)
            .then(response => {
                commit('SET_CURRENT_WORKING_BOOKING_STATUS', response?.data?.booking?.status);
                push.success({
                    message: 'Booking status updated successfully',
                });

                return response?.data?.booking ?? true;
            }).catch(error => {
                push.error({
                    message: error?.response?.data?.message || 'There was an error',
                });

                return undefined;
            }).finally(() => {
                commit('SET_BOOKING_LOADING', {subject: 'bookingStatus', loading: false});
            });
    },

    /**
     * Update a bookings' mile allowance
     */
    updateBookingMileAllowance({commit, state}, data: { b_id: string, miles_included: number, miles_extra: number }) {
        commit('SET_BOOKING_LOADING', {subject: 'updateBooking', loading: true});
        commit('CLEAR_BOOKING_FORM_ERRORS');

        axios.put(`/v1/bookings/${data.b_id}/mile-allowance`, data).then(response => {
            commit('SET_CURRENT_WORKING_BOOKING', response?.data?.booking, []);
            push.success({
                message: 'Booking distance allowance updated successfully',
            });
        }).catch(error => {
            push.error({
                message: error?.response?.data?.message || 'There was an error',
            });
            state.bookingForm.errors.set(error?.response?.data?.errors || []);
        }).finally(() => {
            commit('SET_BOOKING_LOADING', {subject: 'updateBooking', loading: false});
        });
    },

    async getTripPhotos({commit}, b_id: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'getTripPhotos', () => axios.get('/v1/trip-photos', {params: {b_id}}));
            commit(types.SET_CURRENT_WORKING_BOOKING_TRIP_PHOTOS, responseData.trip_photos);
            return responseData;
        } catch (error) {
            console.error(error)
        }
    },

    async resyncTripPhotos({commit}, b_id: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'resyncTripPhotos', () => axios.post(`/v1/trip-photos/resync`, {b_id}));
            commit(types.SET_CURRENT_WORKING_BOOKING_TRIP_PHOTOS, responseData.trip_photos);
            push.success({message: 'Trip photos resync successful'});
            return responseData;
        } catch (error) {
            console.error(error)
        }
    },

    /**
     * Delete a trip photo from a reservation
     */
    async deleteTripPhoto({commit}, uuid: string) {
        try {
            await performApiRequest(commit, 'SET_BOOKING_LOADING', 'deleteTripPhotos', () => axios.delete(`/v1/trip-photos/${uuid}`));
            push.success({message: 'Trip photo deleted successfully'});
        } catch (error) {
            const err = error as AxiosError;
            const message = (err?.response?.data as { message?: string })?.message || 'There was an error';
            commit(types.SET_BOOKING_FORM_ERROR, message);
        }
    },

    async deleteTripPhotos({commit}, uuids: string[]) {
        try {
            return await performApiRequest(commit, 'SET_BOOKING_LOADING', 'deleteTripPhotos', () => axios.post('/v1/trip-photos/batch', uuids));
        } catch (error) {
            const err = error as AxiosError;
            const message = (err?.response?.data as { message?: string })?.message || 'There was an error';
            commit(types.SET_BOOKING_FORM_ERROR, message);
        }
    },

    /**
     * Update a trip photo from a reservation
     */
    async updateTripPhoto({commit}, tripPhoto: TripPhotoInterface) {
        try {
            await performApiRequest(commit, 'SET_BOOKING_LOADING', 'updateTripPhoto', () => axios.put(`/v1/trip-photos/${tripPhoto.uuid}`, tripPhoto));

            push.success({message: 'Trip photo update successful'});
        } catch (error) {
            const err = error as AxiosError;
            const message = (err?.response?.data as { message?: string })?.message || 'There was an error';
            commit(types.SET_BOOKING_FORM_ERROR, message);
            console.error(error)
        }
    },

    async startBookingVehicleSwap({commit}, data: BookingVehicleSwapData) {
        commit('CLEAR_BOOKING_FORM_ERRORS');

        try {
            const responseData = await performApiRequest(commit, 'SET_BOOKING_LOADING', 'startBookingVehicleSwap', () => axios.post(`/v1/bookings/${data.b_id}/vehicle-swap`, data));
            commit(types.SET_CURRENT_WORKING_BOOKING, responseData.booking);

            return true;
        } catch (error) {
            const err = error as AxiosError;
            const message = (err?.response?.data as { message?: string })?.message || 'There was an error';
            commit(types.SET_BOOKING_FORM_ERROR, message);
            console.error(error)
        }
    },

    startBookingVehicleSwapStep2({commit, state}, data: BookingVehicleSwapStep2Data) {
        commit('SET_BOOKING_LOADING', {subject: 'startBookingVehicleSwapStep2', loading: true});
        commit('CLEAR_BOOKING_FORM_ERRORS');

        return axios.post(`/v1/bookings/${data.booking.b_id}/vehicle-swap-step-2`, data).then(response => {
            commit(types.SET_CURRENT_WORKING_BOOKING, response?.data?.booking);

            return true;
        }).catch(e => {
            state.bookingForm.errors.set(e?.response?.data?.errors ?? []);

            return false;
        }).finally(() => {
            commit('SET_BOOKING_LOADING', {subject: 'startBookingVehicleSwapStep2', loading: false});
        });
    },

    resendConfirmationEmail({commit}, b_id: string) {
        commit('SET_BOOKING_LOADING', {subject: 'updateTripPhoto', loading: true});

        axios.post(`/v1/bookings/${b_id}/resend-confirmation-email`)
            .then(() => {
                push.success({
                    message: 'Booking confirmation email resent.',
                });
            }).catch(e => {
            let message = e?.response?.data?.message || 'There was an error';

            if (e?.response?.headers?.['retry-after']) {
                message = `${message}. Retry in ${e.response.headers['retry-after']} seconds`;
            }

            push.error({
                message: message,
            });
        }).finally(() => {
            commit('SET_BOOKING_LOADING', {subject: 'updateTripPhoto', loading: false});
        });
    },

    fetchBookingAutomationEvents({commit}, b_id: string) {
        commit('SET_BOOKING_LOADING', {subject: 'getAutomationEvents', loading: true});

        axios.get(`/v1/bookings/${b_id}/automation-events`)
            .then(response => {
                commit('SET_BOOKING_AUTOMATION_EVENTS', response?.data?.events ?? []);
            }).catch(() => {
        }).finally(() => {
            commit('SET_BOOKING_LOADING', {subject: 'getAutomationEvents', loading: false});
        });
    },

    async removeProviderBookingFromFleetwire({commit}, b_id: string) {
        commit('SET_BOOKING_LOADING', {subject: 'manuallyRemoveBooking', loading: true});

        try {
            const response = await axios.delete(`/v1/bookings/${b_id}/manual-delete`);

            push.success({
                message: 'Booking removed from Fleetwire.',
            });

            return response?.data;
        } catch (error) {
            handleApiError(error, 'There was an error removing this booking from Fleetwire');
            throw error;
        } finally {
            commit('SET_BOOKING_LOADING', {subject: 'manuallyRemoveBooking', loading: false});
        }
    },
};

const bookingModule: Module<BookingState, RootState> = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export default bookingModule;
